import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { DANCE_CATEGORIES, CONTEST_TYPES, US_STATES, CA_PROVINCES } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Upload, FilePdf, ImageSquare, X } from '@phosphor-icons/react';
import Seo from '../components/Seo';

const MAX_BYTES = 10 * 1024 * 1024; // 10MB
const ALLOWED = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif', 'image/gif', 'application/pdf'];

export default function Submit() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [form, setForm] = useState({
    name: '', tribe: '', event_type: 'Pow Wow',
    start_date: '', end_date: '',
    address: '', city: '', state: '', country: 'USA',
    venue: '', description: '',
    contact_name: user?.name || '',
    contact_email: user?.email || '',
    contact_phone: '', website: '',
    admission: 'Free', camping: false, indoor: false,
    dance_categories: [], contest_types: [],
    total_prize_pool: '',
  });
  const [flyer, setFlyer] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const toggleArr = (key, val) => {
    setForm(f => ({ ...f, [key]: f[key].includes(val) ? f[key].filter(x => x !== val) : [...f[key], val] }));
  };

  const pickFile = (file) => {
    if (!file) return;
    if (!ALLOWED.includes(file.type)) {
      toast.error('Flyer must be a PDF or image (JPG/PNG/WebP/HEIC)');
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error('Flyer too large — max 10 MB');
      return;
    }
    setFlyer(file);
  };

  const onFileChange = (e) => pickFile(e.target.files?.[0]);
  const onDrop = (e) => {
    e.preventDefault();
    pickFile(e.dataTransfer.files?.[0]);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.city || !form.state || !form.start_date || !form.end_date) {
      toast.error('Please fill in event name, dates and location');
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => {
        if (Array.isArray(v)) fd.append(k, v.join(','));
        else if (typeof v === 'boolean') fd.append(k, v ? 'true' : 'false');
        else if (v !== '' && v != null) fd.append(k, v);
      });
      if (flyer) fd.append('flyer', flyer);
      const r = await api.post('/events/submit-public', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (r.data?.email_disabled) {
        toast.success('Submission received! (Email delivery not yet configured — your submission is saved and will be reviewed.)', { duration: 6000 });
      } else if (r.data?.email_sent) {
        toast.success('Submitted! We just emailed your details to our reviewers — they\'ll be in touch within 24 hours.');
      } else {
        toast.success('Submission received! Our team will review within 24 hours.');
      }
      navigate('/');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <Seo
        path="/submit"
        title="Submit Your Pow Wow"
        description="Are you organizing a pow wow? Submit your event to be listed on Pow Wow Finder — free. Upload a flyer, add dates & details, and reach thousands of dancers, drum groups, and vendors."
        keywords="submit powwow, list a pow wow, powwow submission, event listing, powwow organizer"
      />
      <p className="label text-terracotta mb-2">Organizer portal</p>
      <h1 className="font-display text-4xl sm:text-5xl tracking-tight mb-2">Submit your pow wow</h1>
      <p className="text-muted-foreground mb-10">
        Fill out the details below and upload a flyer. We'll review your submission and reach out within 24 hours. No account needed.
      </p>

      <form onSubmit={submit} className="space-y-10" data-testid="submit-form">
        <fieldset className="space-y-4">
          <legend className="font-display text-2xl mb-2">Event basics</legend>
          <Input required value={form.name} onChange={e => set('name', e.target.value)} placeholder="Event name *" data-testid="submit-name" />
          <Input value={form.tribe} onChange={e => set('tribe', e.target.value)} placeholder="Host nation / tribe" data-testid="submit-tribe" />
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Start date *</label>
              <Input required type="date" value={form.start_date} onChange={e => set('start_date', e.target.value)} data-testid="submit-start" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">End date *</label>
              <Input required type="date" value={form.end_date} onChange={e => set('end_date', e.target.value)} data-testid="submit-end" />
            </div>
          </div>
          <Textarea rows={4} value={form.description} onChange={e => set('description', e.target.value)} placeholder="Describe the event, history, what makes it special…" data-testid="submit-description" />
        </fieldset>

        <fieldset className="space-y-4">
          <legend className="font-display text-2xl mb-2">Location</legend>
          <Input value={form.venue} onChange={e => set('venue', e.target.value)} placeholder="Venue (e.g. Memorial Park)" data-testid="submit-venue" />
          <Input value={form.address} onChange={e => set('address', e.target.value)} placeholder="Street address" data-testid="submit-address" />
          <div className="grid sm:grid-cols-3 gap-4">
            <Input required value={form.city} onChange={e => set('city', e.target.value)} placeholder="City *" data-testid="submit-city" />
            <Select value={form.state} onValueChange={v => set('state', v)}>
              <SelectTrigger data-testid="submit-state"><SelectValue placeholder="State / Province *" /></SelectTrigger>
              <SelectContent className="max-h-64">{[...US_STATES, ...CA_PROVINCES].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={form.country} onValueChange={v => set('country', v)}>
              <SelectTrigger data-testid="submit-country"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="USA">USA</SelectItem><SelectItem value="Canada">Canada</SelectItem></SelectContent>
            </Select>
          </div>
          <p className="text-xs text-muted-foreground">We'll geolocate the address automatically — you don't need to provide coordinates.</p>
        </fieldset>

        {/* Flyer Upload */}
        <fieldset className="space-y-4">
          <legend className="font-display text-2xl mb-2">Flyer (optional)</legend>
          <p className="text-sm text-muted-foreground -mt-2">Attach a PDF or image flyer (max 10 MB). Helps reviewers approve your event faster.</p>

          {!flyer ? (
            <label
              onDragOver={e => e.preventDefault()}
              onDrop={onDrop}
              className="flex flex-col items-center justify-center bg-card border-2 border-dashed border-border hover:border-turquoise rounded-2xl py-12 px-6 cursor-pointer transition group"
              data-testid="flyer-dropzone"
            >
              <Upload size={36} weight="duotone" className="text-turquoise mb-3 group-hover:scale-110 transition" />
              <p className="font-semibold text-bone mb-1">Click or drag a flyer here</p>
              <p className="text-xs text-muted-foreground">PDF, JPG, PNG, WebP or HEIC · max 10 MB</p>
              <input
                ref={fileRef}
                type="file"
                accept="application/pdf,image/jpeg,image/png,image/webp,image/heic,image/heif,image/gif"
                onChange={onFileChange}
                className="hidden"
                data-testid="flyer-input"
              />
            </label>
          ) : (
            <div className="flex items-center gap-4 bg-card border border-turquoise/40 rounded-2xl p-4" data-testid="flyer-preview">
              <div className="w-14 h-14 rounded-xl bg-turquoise/10 grid place-items-center text-turquoise flex-shrink-0">
                {flyer.type === 'application/pdf'
                  ? <FilePdf size={28} weight="duotone" />
                  : <ImageSquare size={28} weight="duotone" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{flyer.name}</p>
                <p className="text-xs text-muted-foreground">
                  {(flyer.size / 1024).toFixed(0)} KB · {flyer.type.replace('application/', '').replace('image/', '').toUpperCase()}
                </p>
              </div>
              <button
                type="button"
                onClick={() => { setFlyer(null); if (fileRef.current) fileRef.current.value = ''; }}
                className="w-9 h-9 rounded-full bg-muted hover:bg-crimson/20 hover:text-crimson grid place-items-center transition"
                aria-label="Remove flyer"
                data-testid="flyer-remove"
              >
                <X size={16} />
              </button>
            </div>
          )}
        </fieldset>

        <fieldset className="space-y-4">
          <legend className="font-display text-2xl mb-2">Categories & contests</legend>
          <div>
            <label className="label text-muted-foreground block mb-2">Dance categories</label>
            <div className="flex flex-wrap gap-2">
              {DANCE_CATEGORIES.map(c => (
                <button type="button" key={c} onClick={() => toggleArr('dance_categories', c)}
                  className={`rounded-full px-4 py-1.5 text-sm border transition ${form.dance_categories.includes(c) ? 'bg-turquoise text-navy border-turquoise' : 'bg-card border-border hover:border-turquoise'}`}>
                  {c}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="label text-muted-foreground block mb-2">Contest types</label>
            <div className="flex flex-wrap gap-2">
              {CONTEST_TYPES.map(c => (
                <button type="button" key={c} onClick={() => toggleArr('contest_types', c)}
                  className={`rounded-full px-4 py-1.5 text-sm border transition ${form.contest_types.includes(c) ? 'bg-crimson text-bone border-crimson' : 'bg-card border-border hover:border-crimson'}`}>
                  {c}
                </button>
              ))}
            </div>
          </div>
          <Input type="number" value={form.total_prize_pool} onChange={e => set('total_prize_pool', e.target.value)} placeholder="Total prize pool ($)" data-testid="submit-prize" />
        </fieldset>

        <fieldset className="space-y-4">
          <legend className="font-display text-2xl mb-2">Contact & details</legend>
          <div className="grid sm:grid-cols-2 gap-4">
            <Input value={form.contact_name} onChange={e => set('contact_name', e.target.value)} placeholder="Your name" data-testid="submit-contact-name" />
            <Input value={form.contact_email} onChange={e => set('contact_email', e.target.value)} placeholder="Contact email *" type="email" required data-testid="submit-email" />
          </div>
          <Input value={form.contact_phone} onChange={e => set('contact_phone', e.target.value)} placeholder="Contact phone" data-testid="submit-phone" />
          <Input value={form.website} onChange={e => set('website', e.target.value)} placeholder="Website / social URL" data-testid="submit-website" />
          <Input value={form.admission} onChange={e => set('admission', e.target.value)} placeholder="Admission (e.g. Free, $10)" data-testid="submit-admission" />
          <div className="flex flex-wrap gap-6">
            <label className="flex items-center gap-2 text-sm"><Checkbox checked={form.indoor} onCheckedChange={v => set('indoor', !!v)} data-testid="submit-indoor" /> Indoor venue</label>
            <label className="flex items-center gap-2 text-sm"><Checkbox checked={form.camping} onCheckedChange={v => set('camping', !!v)} data-testid="submit-camping" /> Camping available</label>
          </div>
        </fieldset>

        <div className="flex flex-wrap items-center gap-4 pt-4">
          <Button type="submit" disabled={submitting} className="bg-turquoise hover:bg-turquoise/90 text-navy rounded-full px-8 h-12 font-semibold" data-testid="submit-form-btn">
            {submitting ? 'Sending…' : 'Submit pow wow'}
          </Button>
          <p className="text-xs text-muted-foreground">
            Your submission will be emailed to our reviewers. We respond within 24 hours.
          </p>
        </div>
      </form>
    </div>
  );
}
